/**
 * =============================================================================
 * IDS CRM - Returning to a list as you left it
 * =============================================================================
 *
 * A list page (Jobs & Tickets, Assets, Customers, ...) remembers how it was
 * last left: its address (which carries any sort or filter kept there) and
 * the filters it holds on screen only. Opening a row and coming back with a
 * "←" link, or with the browser's Back button, shows the list as it was left.
 * Opening the list fresh (the sidebar, a dashboard link, a bookmark, a typed
 * address) starts clean.
 *
 * A page that is opened from more than one list (a job from Jobs & Tickets or
 * from Scheduling) also remembers which list it was opened from, so its "←"
 * link goes back there. Opened from anywhere else, it goes to its usual list.
 *
 * Kept in sessionStorage: one browser tab, gone when the tab closes, and
 * wiped when someone signs in or out. Storage can be missing or full (private
 * windows), so every read and write is guarded and the list simply opens
 * fresh.
 * =============================================================================
 */

const KEY_PREFIX = 'ids-crm-list:';
const ORIGIN_PREFIX = 'ids-crm-list-origin:';
const OWNER_KEY = 'ids-crm-list-owner';

// A tab left open overnight should not bring back yesterday's filters.
export const LIST_MEMORY_HOURS = 12;

/** Every list page that remembers itself. A page opened from one of these returns to it. */
export const LIST_PATHS = [
  '/tickets', '/tasks', '/customers', '/distributors', '/assets',
  '/engineers', '/pipeline', '/bulletins', '/accommodation'
];

const isPlainObject = (value) => Boolean(value) && typeof value === 'object' && !Array.isArray(value);

const readRaw = (path) => {
  const raw = window.sessionStorage.getItem(KEY_PREFIX + path);
  if (!raw) return null;
  const saved = JSON.parse(raw);
  if (!isPlainObject(saved) || typeof saved.at !== 'number') return null;
  return {
    search: typeof saved.search === 'string' ? saved.search : '',
    view: isPlainObject(saved.view) ? saved.view : {},
    at: saved.at
  };
};

/** What was remembered for the list at `path`, or null. */
export function readList(path) {
  try {
    const saved = readRaw(path);
    if (!saved) return null;
    if (Date.now() - saved.at > LIST_MEMORY_HOURS * 60 * 60 * 1000) return null;
    return saved;
  } catch (error) {
    return null;
  }
}

/** Remember how the list at `path` looks now. */
export function rememberList(path, { search = '', view = {} } = {}) {
  try {
    window.sessionStorage.setItem(
      KEY_PREFIX + path,
      JSON.stringify({ search, view, at: Date.now() })
    );
  } catch (error) {
    // No storage: the list opens fresh next time.
  }
}

/**
 * The list is being left now: its memory counts from this moment. A list that
 * sat on screen all night still comes back as it was when a row is opened in
 * the morning. A memory already wiped (signing out) stays wiped.
 */
export function touchList(path) {
  try {
    const saved = readRaw(path);
    if (!saved) return;
    window.sessionStorage.setItem(KEY_PREFIX + path, JSON.stringify({ ...saved, at: Date.now() }));
  } catch (error) {
    // No storage: nothing to keep.
  }
}

/** The list the page at `pagePath` was opened from, or null. */
export function readOrigin(pagePath) {
  try {
    const origin = window.sessionStorage.getItem(ORIGIN_PREFIX + pagePath);
    return LIST_PATHS.includes(origin) ? origin : null;
  } catch (error) {
    return null;
  }
}

const rememberOrigin = (pagePath, listPath) => {
  try {
    window.sessionStorage.setItem(ORIGIN_PREFIX + pagePath, listPath);
  } catch (error) {
    // No storage: the page returns to its usual list.
  }
};

const forgetOrigin = (pagePath) => {
  try {
    window.sessionStorage.removeItem(ORIGIN_PREFIX + pagePath);
  } catch (error) {
    // No storage: nothing was remembered.
  }
};

// '/tasks/7/edit' and '/tasks/new' are both pages of the 'tasks' kind.
const sectionOf = (path) => String(path).split('/')[1] || '';

/**
 * Notes where a page was opened from, as the app moves from `fromPath` to
 * `toPath` (components/ListOriginTracker calls this on every move).
 *  - Opened from a list: that list is where its "←" link returns.
 *  - Opened from a page of the same kind (a job, then its edit form, then
 *    the job again; a new-work form, then the saved job): it keeps that
 *    page's list.
 *  - Opened from anywhere else (Today, a customer, a search): it has none,
 *    and returns to its usual list.
 *  - The browser's Back and Forward change nothing: the page keeps what it had.
 */
export function trackNavigation(fromPath, toPath, navigationType) {
  if (!fromPath || !toPath || fromPath === toPath) return;
  if (navigationType === 'POP') return;
  if (LIST_PATHS.includes(toPath)) return;
  if (LIST_PATHS.includes(fromPath)) {
    rememberOrigin(toPath, fromPath);
    return;
  }
  const inherited = sectionOf(fromPath) === sectionOf(toPath) ? readOrigin(fromPath) : null;
  if (inherited) rememberOrigin(toPath, inherited);
  else forgetOrigin(toPath);
}

/**
 * Where a "←" link should go. `paths` are the lists this page can be opened
 * from, its usual one first. The list the page at `pagePath` was opened from
 * wins when it is one of them; otherwise the usual list. Either way the link
 * carries the address that list was left on.
 */
export function listReturn(paths, pagePath) {
  const candidates = Array.isArray(paths) ? paths : [paths];
  const origin = pagePath ? readOrigin(pagePath) : null;
  const path = candidates.includes(origin) ? origin : candidates[0];
  const saved = readList(path);
  return { path, to: path + (saved ? saved.search : '') };
}

/**
 * Forget every list, and where every page was opened from. Called when
 * someone signs in or out, so the next person on this browser tab never gets
 * the last person's filters.
 */
export function forgetLists() {
  try {
    const store = window.sessionStorage;
    const keys = [];
    for (let index = 0; index < store.length; index += 1) keys.push(store.key(index));
    keys
      .filter(key => key && (key.startsWith(KEY_PREFIX) || key.startsWith(ORIGIN_PREFIX)))
      .forEach(key => store.removeItem(key));
  } catch (error) {
    // No storage: nothing was remembered.
  }
}

/**
 * The lists in this tab belong to whoever left them. When the signed-in
 * person turns out to be someone else (they signed in from another tab), the
 * memory is wiped before they see it.
 */
export function claimLists(userId) {
  if (userId === undefined || userId === null) return;
  try {
    const owner = window.sessionStorage.getItem(OWNER_KEY);
    if (owner && owner !== String(userId)) forgetLists();
    window.sessionStorage.setItem(OWNER_KEY, String(userId));
  } catch (error) {
    // No storage: nothing was remembered.
  }
}

/**
 * A typed address, a bookmark or a link from outside the CRM opens a new
 * page load, and that is a fresh visit: every list starts clean. A reload,
 * or the browser's Back into the CRM, keeps them.
 */
export function forgetListsOnFreshLoad() {
  try {
    const [entry] = window.performance.getEntriesByType('navigation');
    if (entry && entry.type === 'navigate') forgetLists();
  } catch (error) {
    // No timing in this browser: the lists are kept.
  }
}

forgetListsOnFreshLoad();

/** Passed with every return to a list, so the list knows to restore itself. */
export const RESTORE_LIST_STATE = { restoreList: true };
